import React, { useEffect, useState } from 'react';
import { BellRing, BellOff } from 'lucide-react';
import { toast } from 'sonner';
import { getProgramReminderTimes, setProgramReminderTimes, setProgramReminders } from '../../lib/programsApi';

const fmt = (hhmm) => {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, '0')} ${ampm}`;
};

export const ProgramReminderTimes = ({ slug }) => {
  const [st, setSt] = useState(null);
  const [edit, setEdit] = useState(false);
  const [m, setM] = useState('');
  const [e, setE] = useState('');
  useEffect(() => { getProgramReminderTimes(slug).then(setSt).catch(() => {}); }, [slug]);
  if (!st) return null;
  const morning = st.morningOverride || st.globalMorning;
  const evening = st.eveningOverride || st.globalEvening;
  const custom = !!(st.morningOverride || st.eveningOverride);

  const save = async () => {
    try {
      const r = await setProgramReminderTimes(slug, { morning: m || null, evening: e || null });
      setSt({ ...st, morningOverride: r.morningReminderTimeOverride, eveningOverride: r.eveningReminderTimeOverride });
      setEdit(false); toast.success('Reminder times updated for this journey');
    } catch (err) { toast.error(err.message); }
  };
  const useDefaults = async () => {
    try { await setProgramReminderTimes(slug, { morning: null, evening: null }); setSt({ ...st, morningOverride: null, eveningOverride: null }); setEdit(false); toast.success('Using your default notification times'); }
    catch (err) { toast.error(err.message); }
  };
  const toggle = async () => {
    const next = !st.enabled;
    setSt({ ...st, enabled: next });
    try { await setProgramReminders(next); toast.success(next ? 'Program reminders on' : 'Program reminders off'); } catch { setSt({ ...st, enabled: !next }); }
  };

  return (
    <div className="rounded-2xl border border-[#e3d2a8]/60 px-3 py-2.5 mb-3" data-testid="program-reminder-times">
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center gap-2 text-xs font-semibold text-[#1e3a5f]">{st.enabled ? <BellRing className="w-3.5 h-3.5 text-[#D6AD60]" /> : <BellOff className="w-3.5 h-3.5 text-[#7a9b8e]" />} Program reminders {st.enabled ? '' : '(off)'}</span>
        <button type="button" onClick={toggle} className={`w-9 h-5 rounded-full relative transition-colors ${st.enabled ? 'bg-[#D6AD60]' : 'bg-stone-300'}`} aria-label="Toggle program reminders" data-testid="flagship-reminders-toggle"><span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-[left] ${st.enabled ? 'left-[18px]' : 'left-0.5'}`} /></button>
      </div>
      {st.enabled && !edit && (
        <div className="mt-2 text-[11px] text-[#2d4a70]">
          <p>Morning reminder: <b data-testid="program-reminder-morning">{fmt(morning)}</b> · Evening reminder: <b data-testid="program-reminder-evening">{fmt(evening)}</b>{custom ? ' · custom for this journey' : ' · your default times'}</p>
          <div className="flex gap-3 mt-1">
            <button type="button" onClick={() => { setM(morning); setE(evening); setEdit(true); }} className="font-semibold text-[#a88a4c]" data-testid="program-reminder-change">Change times</button>
            {custom && <button type="button" onClick={useDefaults} className="font-semibold text-[#7a9b8e]" data-testid="program-reminder-use-defaults">Use my default notification times</button>}
          </div>
        </div>
      )}
      {st.enabled && edit && (
        <div className="mt-2 grid grid-cols-2 gap-2 text-[11px]" data-testid="program-reminder-editor">
          <label className="text-[#2d4a70]">Morning<input type="time" value={m} onChange={(ev) => setM(ev.target.value)} className="mt-0.5 w-full h-9 rounded-lg border border-[#e3d2a8]/60 bg-white px-2 text-sm text-[#0f1e33]" data-testid="program-reminder-morning-input" /></label>
          <label className="text-[#2d4a70]">Evening<input type="time" value={e} onChange={(ev) => setE(ev.target.value)} className="mt-0.5 w-full h-9 rounded-lg border border-[#e3d2a8]/60 bg-white px-2 text-sm text-[#0f1e33]" data-testid="program-reminder-evening-input" /></label>
          <button type="button" onClick={() => setEdit(false)} className="h-9 rounded-full border border-[#0f1e33]/20 text-[#0f1e33] font-semibold uppercase tracking-[0.12em]">Cancel</button>
          <button type="button" onClick={save} className="h-9 rounded-full bg-[#0f1e33] text-[#f9f4ea] font-semibold uppercase tracking-[0.12em]" data-testid="program-reminder-save">Save for this journey</button>
          <p className="col-span-2 text-[10px] text-[#7a9b8e]">Only this journey changes — your global reflection times stay the same.</p>
        </div>
      )}
    </div>
  );
};
